import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query'
import {
  createTRPCProxyClient,
  httpBatchLink,
  httpLink,
  splitLink,
} from '@trpc/client'
import type { Operation } from '@trpc/client'
import React, { useEffect } from 'react'
import { isTRPCClientError, RouterOutputs, trpc } from './trpc'
import {
  buildRequestHeaders,
  isTokenRefreshOperation,
  NonEmptyArray,
  skipsTokenRefresh,
} from './authHeaders'
import AppConfig from 'config'
import type { AppRouter } from '@ca/api'
import useSession from 'hooks/useSession'
import { invalidateSession } from 'hooks/useLogout'
import { createToast } from '@ui/toasts/useToastManager'
import i18n from 'i18next'
import { debugAuth } from '@order/utils/getDebugLog'
import SessionUtils from '@order/utils/SessionUtils'
import { createTRPCQueryUtils } from '@trpc/react-query'

// import { useReactQueryDevTools } from '@dev-plugins/react-query'

const handleUnauthorizedError = () => {
  const session = useSession.getState()
  debugAuth.warn('Unauthorized Error for session', session)
  invalidateSession()
  // We are not logged in at all so we don't need to show a toast?
  if (!session.staff) {
    return
  }
  createToast({
    text: i18n.t('toasts:error.unauthorized.desc'),
    title: i18n.t('toasts:error.default.title'),
    variant: 'error',
  })
}

// Global error handling
const queryCache = new QueryCache({
  onError: (error) => {
    if (!isTRPCClientError(error)) {
      return
    }
    if (error.data?.code === 'UNAUTHORIZED') {
      handleUnauthorizedError()
    }
  },
})

export const queryClient = new QueryClient({
  queryCache,
  defaultOptions: {
    queries: {
      retry(failureCount, error) {
        // Don't try to retry on 401
        if (isTRPCClientError(error) && error.data?.code === 'UNAUTHORIZED') {
          return false
        }
        return failureCount < 3
      },
    },
  },
})

async function refreshToken({
  type,
  refresh_token,
}: {
  type: 'customer' | 'staff' | 'store'
  refresh_token?: string
}) {
  if (!refresh_token) {
    debugAuth.log(`Refresh token for ${type} not available`)
    return
  }
  const res = await trpcProxyClient.refreshToken.mutate({
    type,
    refresh_token,
  })
  const { updateTokens } = useSession.getState()
  updateTokens(res, type)
  debugAuth.log(`Refreshed token for ${type}`)
  return res
}

const getTime = () => {
  // We need to get the data from raw query client
  const q = queryClient.getQueryState<RouterOutputs['health']>([
    ['health'],
    { input: {}, type: 'query' },
  ])
  if (!q?.data) {
    return {
      ms: Date.now(),
      type: 'client,',
    }
  }
  const diff = Date.now() - q.dataUpdatedAt
  const serverTime = q.data.timestampMs + diff
  return {
    ms: serverTime,
    type: 'server',
  }
}

export async function getTokens() {
  const { customer, staff, store } = useSession.getState()
  const time = getTime()
  const tokens = {
    customer: customer?.token,
    staff: staff?.token,
    store: store?.token,
  }

  debugAuth.log('Checking refresh with', time)
  if (SessionUtils.shouldRefresh(customer, time?.ms)) {
    const res = await refreshToken({
      type: 'customer',
      refresh_token: customer?.refresh_token,
    })
    if (res) {
      tokens.customer = res.token
    }
  }
  if (SessionUtils.shouldRefresh(store)) {
    const res = await refreshToken({
      type: 'store',
      refresh_token: store?.refresh_token,
    })
    if (res) {
      tokens.store = res.token
    }
  }
  if (SessionUtils.shouldRefresh(staff)) {
    const res = await refreshToken({
      type: 'staff',
      refresh_token: staff?.refresh_token,
    })
    if (res) {
      tokens.staff = res.token
    }
  }

  return tokens
}

async function getHeaders(opts: { opList: NonEmptyArray<Operation> }) {
  // Auth-free operations must be checked before getTokens triggers a refresh.
  if (skipsTokenRefresh(opts.opList)) {
    return buildRequestHeaders({}, opts)
  }
  const tokens = await getTokens()
  return buildRequestHeaders(tokens, opts)
}

const trpcUrl = `${AppConfig.apiUrl}/api/trpc`

const createLinks = () => [
  splitLink<AppRouter>({
    condition: isTokenRefreshOperation,
    // Refresh must be isolated from authenticated batches and header refreshes.
    true: httpLink<AppRouter>({
      url: trpcUrl,
      headers: () => buildRequestHeaders({}),
    }),
    false: httpBatchLink<AppRouter>({
      url: trpcUrl,
      headers: getHeaders,
    }),
  }),
]

export const trpcClient = trpc.createClient({
  links: createLinks(),
})

export const trpcClientUtils = createTRPCQueryUtils({
  queryClient,
  client: trpcClient,
})

// For use outside of react components
export const trpcProxyClient = createTRPCProxyClient<AppRouter>({
  links: createLinks(),
})

export default function ApiProvider({
  children,
}: {
  children: React.ReactNode
}) {
  useEffect(() => {
    if (!AppConfig.isDev) {
      return
    }
    const session = useSession.getState()
    if (!session.customer) {
      return
    }
    const refreshDevCustomer = async () => {
      try {
        const res = await trpcProxyClient.customer.refresh.mutate()
        useSession.getState().setCustomer(res)
      } catch (error) {
        debugAuth.warn('Dev startup customer refresh failed', error)
      }
    }
    refreshDevCustomer()
  }, [])

  // useReactQueryDevTools(queryClient)
  useEffect(() => {
    // Refresh tokens every 15 minutes
    const interval = setInterval(() => {
      getTokens()
    }, 1000 * 60 * 15)
    return () => clearInterval(interval)
  }, [])

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </trpc.Provider>
  )
}
